"use client";

import { clsx } from "clsx";
import * as RadioGroup from "@radix-ui/react-radio-group";

export type SegmentedRadioGroupProps = {
  value: string | undefined;
  onChange: (value: string) => void;
  options: { label: string; value: string }[];
  disabled?: boolean;
  /** Applied to the root pill container (e.g. `mb-3.5` for layout margin). */
  className?: string;
} & (
  | { ariaLabel: string; ariaLabelledby?: never }
  | { ariaLabel?: never; ariaLabelledby: string }
);

/**
 * A horizontally-arranged segmented radio group implemented with Radix UI
 * RadioGroup. Provides roving tabindex, Arrow / Home / End keyboard navigation,
 * and correct ARIA semantics (`role="radiogroup"` + `role="radio"` +
 * `aria-checked`) automatically.
 *
 * Styled to match the pill control used in `QuizStepPriorities`. Both callers
 * converge on a single pill look; the only per-instance customisation is an
 * optional root `className` for layout (e.g. `mb-3.5` in `EnquireModal`).
 */
export function SegmentedRadioGroup({
  value,
  onChange,
  options,
  ariaLabel,
  ariaLabelledby,
  disabled,
  className,
}: SegmentedRadioGroupProps) {
  return (
    <RadioGroup.Root
      value={value ?? ""}
      onValueChange={onChange}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      disabled={disabled}
      className={clsx(
        "inline-flex rounded-full p-[3px] bg-bush-50 border border-border",
        className,
      )}
    >
      {options.map((option) => {
        // Derive selected state from the controlled `value` prop (not Radix data-state).
        // This component must always be used as a controlled component.
        const sel = value === option.value;
        return (
          <RadioGroup.Item
            key={option.value}
            value={option.value}
            className={clsx(
              "rounded-full px-3.5 py-1.5 text-base font-semibold whitespace-nowrap border-0 font-[inherit]",
              // focus-ring-tight: the group's p-[3px] can't host an offset ring —
              // an offset would draw through the container border (TRA-288 rule).
              "transition-colors focus-ring-tight cursor-pointer disabled:cursor-not-allowed disabled:opacity-50",
              sel ? "text-white bg-primary" : "text-primary bg-transparent",
            )}
          >
            {option.label}
          </RadioGroup.Item>
        );
      })}
    </RadioGroup.Root>
  );
}
